import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Download, FileText, ArrowRight, QrCode, Check, Mail, Smartphone } from "lucide-react";
import { toast } from "react-toastify";

export default function SuccessState({
  applicationId,
  registrationId,
  eventDetails,
  verificationState,
  locationState,
  onDownloadReceipt,
  onNavigate,
}) {
  const [resendingInvite, setResendingInvite] = useState(false);
  const [downloadingInvite, setDownloadingInvite] = useState(false);

  const isTradeMission = eventDetails?.event_type?.some(
    (type) => type === "trade_mission" || type === "trade mission"
  );

  const getMessage = () => {
    if (isTradeMission) {
      return "We've received your registration. You'll receive an invitation via SMS or email (with download link) containing your QR code, event details, and attendee info.";
    }
    if (verificationState === "verified" && locationState?.paymentVerified !== undefined) {
      if (locationState?.isFreeTicket === true) {
        return "Your free registration is confirmed. An invitation will be sent via SMS or email with a download link.";
      }
      return "Payment confirmed. An invitation will be sent via SMS or email (with download link) to each attendee.";
    }
    return "We've received your registration. You'll receive an invitation via SMS or email (with download link) shortly.";
  };

  const handleDownloadInvitation = async () => {
    setDownloadingInvite(true);
    try {
      // Backend: e.g. GET /user/events/registration/:registrationId/invitation/download
      await new Promise((r) => setTimeout(r, 1200));
      toast.info("Invitation download will be available once backend is connected.");
    } catch (e) {
      toast.error("Failed to download invitation");
    } finally {
      setDownloadingInvite(false);
    }
  };

  const handleResendInvitation = async (channel) => {
    setResendingInvite(true);
    try {
      // Backend: e.g. POST .../invitation/resend { channel: "email" | "sms" }
      await new Promise((r) => setTimeout(r, 1200));
      toast.info(`${channel === "email" ? "Email" : "SMS"} invitation will be resent once backend is connected.`);
    } catch (e) {
      toast.error("Failed to resend invitation");
    } finally {
      setResendingInvite(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="text-center"
    >
      <h1 className="mb-2 text-3xl font-bold text-gray-900 sm:text-4xl">
        Registration Successful!
      </h1>

      {applicationId && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-4 px-2 text-sm font-medium text-gray-600 break-all min-w-0 max-w-full"
        >
          Application ID: <span className="font-mono font-semibold">{applicationId}</span>
        </motion.p>
      )}

      {eventDetails?.event_title && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="mb-8 text-lg font-semibold text-orange-600"
        >
          {eventDetails.event_title}
        </motion.p>
      )}

      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
        className="flex justify-center mb-6"
      >
        <div className="relative">
          <div className="flex justify-center items-center w-32 h-32 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full border-4 border-orange-300 shadow-xl">
            <CheckCircle2 size={64} className="text-white drop-shadow-lg" />
          </div>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ delay: 0.5, duration: 2, repeat: Infinity }}
            className="absolute inset-0 bg-orange-400 rounded-full opacity-20 animate-pulse"
          />
        </div>
      </motion.div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mb-2 text-xl font-bold text-gray-900"
      >
        Your registration has been confirmed successfully!
      </motion.p>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mb-8 text-base text-gray-600"
      >
        {getMessage()}
      </motion.p>

      {/* Your invitation – same as event success (conference/trade) */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
        className="mb-6 p-5 text-left bg-white rounded-2xl border-2 border-teal-200 shadow-lg max-w-lg mx-auto"
      >
        <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
          <QrCode size={20} className="text-teal-600" />
          Your invitation
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          We send <strong>individual invitations</strong> via <strong>SMS</strong> or <strong>Email</strong> (with a download link) to each attendee.
        </p>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Each invitation includes:</p>
        <ul className="grid grid-cols-1 gap-1.5 mb-4 text-sm text-gray-700 sm:grid-cols-2">
          <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Unique QR Code / Ticket ID</li>
          <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Conference name & organizer</li>
          <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Start & end date & time</li>
          <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Venue</li>
          <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Attendee name, organization</li>
          <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Email, phone, address</li>
          <li className="flex items-center gap-2"><Check size={14} className="text-teal-500 flex-shrink-0" /> Country & city</li>
        </ul>
        <div className="flex flex-wrap gap-2 justify-center">
          <motion.button
            onClick={handleDownloadInvitation}
            disabled={downloadingInvite}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-teal-700 bg-teal-50 rounded-xl border border-teal-200 hover:bg-teal-100 disabled:opacity-50"
          >
            <Download size={16} />
            Download invitation
          </motion.button>
          <motion.button
            onClick={() => handleResendInvitation("email")}
            disabled={resendingInvite}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl border border-gray-200 hover:bg-gray-200 disabled:opacity-50"
          >
            <Mail size={16} />
            Resend to email
          </motion.button>
          <motion.button
            onClick={() => handleResendInvitation("sms")}
            disabled={resendingInvite}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl border border-gray-200 hover:bg-gray-200 disabled:opacity-50"
          >
            <Smartphone size={16} />
            Resend via SMS
          </motion.button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="flex flex-col gap-3 justify-center items-center sm:flex-row"
      >
        {!isTradeMission && (
          <motion.button
            onClick={onDownloadReceipt}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex gap-2 items-center px-6 py-3 text-sm font-semibold text-orange-700 rounded-lg border-2 border-orange-300 transition-all bg-orange-50/80 hover:bg-orange-100 hover:border-orange-400"
          >
            <Download size={18} />
            Download Receipt
          </motion.button>
        )}

        <motion.button
          onClick={() => onNavigate("/my-registrations")}
          whileHover={{ scale: 1.02, x: 4 }}
          whileTap={{ scale: 0.98 }}
          className="inline-flex gap-2 items-center px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-xl transition-all hover:from-orange-600 hover:to-orange-700 hover:shadow-2xl"
        >
          <FileText size={18} />
          View My Registrations
          <ArrowRight size={16} />
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

